import type { FlightCardData, SearchFlightsRequest } from '@massilia/queries'
import type { z } from 'zod'

import {
  getApiErrorMessage,
  getRateLimitScope,
  isExpiredSearchSessionError,
  isProviderUnavailableError,
  useCreateBookingSession,
  useDebugAccessQuery,
  useSearchFlightsQuery,
} from '@massilia/queries'
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useIsMobile,
} from '@massilia/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { format } from 'date-fns'
import { ArrowUpDown } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import type { DurationRange } from '@/components/flights/filters/duration-filter'
import type { PriceRange } from '@/components/flights/filters/price-filter'
import type { FlightSearchSchemaType } from '@/lib/schemas'

import { toPricePreview } from '@/components/booking/price-preview'
import { SessionExpiredError } from '@/components/booking/session-expired-error'
import { DebugProvider } from '@/components/flights/debug-context'
import { FilteredEmptyCard } from '@/components/flights/filtered-empty-card'
import {
  applyFilters,
  boundsToRange,
  FiltersSkeleton,
  FiltersStateProvider,
  FlightFilters,
  type FlightFiltersState,
  parseFilterTimeRanges,
  RESET_FILTERS_PATCH,
  sortOffers,
} from '@/components/flights/filters'
import { FiltersModal } from '@/components/flights/filters/filters-modal'
import { FlightCardSkeleton } from '@/components/flights/flight-card-skeleton'
import { FlightErrorCard } from '@/components/flights/flight-error-card'
import { FlightList } from '@/components/flights/flight-list'
import { FlightSearchLoadingModal } from '@/components/flights/flight-search-loading-modal'
import { MobileResultsToolbar } from '@/components/flights/mobile-results-toolbar'
import { NoResultsCard } from '@/components/flights/no-results-card'
import { ResultsBackHome } from '@/components/flights/results-back-home'
import { ResultsBanners } from '@/components/flights/results-banners'
import { SearchFormOverlay } from '@/components/flights/search-form-overlay'
import { buildFlightSearchSummary } from '@/components/flights/search-summary'
import { FlightSearchForm } from '@/components/research-bar/-form-groups/flight-search-form'
import { BackToTopButton } from '@/components/ui/back-to-top-button'
import { useRestartFlightSearch } from '@/lib/restart-flight-search'
import {
  DEFAULT_OPTION,
  flightSearchSchema,
} from '@/lib/schemas/flight-research-bar/flight-research-bar-base.schema'
import { flightResultsSearchSchema } from '@/lib/schemas/flight-results-search.schema'
import { useAuthStore } from '@/stores/auth.store'
import { useBookingPricePreviewStore } from '@/stores/booking-price-preview.store'
import { useFlightSearchStore } from '@/stores/flight-search.store'
import { useFilters } from '@/utils/use-search-params'

// URL search params type inferred from Zod schema
export type FlightResultsSearch = z.infer<typeof flightResultsSearchSchema>

export const Route = createFileRoute('/_booking/vols/resultats')({
  component: FlightResultsPage,
  validateSearch: flightResultsSearchSchema,
})

// Default flight search values if parsing fails
const DEFAULT_FLIGHT_SEARCH: FlightSearchSchemaType = {
  flight: [
    {
      date: {
        from: new Date(),
      },
      departure: DEFAULT_OPTION,
      destination: DEFAULT_OPTION,
    },
  ],
  passengers: {
    adults: 1,
    babies: 0,
    infants: 0,
    seniors: 0,
    youngsters: 0,
  },
  travelClass: 'ECONOMY',
  type: 'one-way',
}

function FlightResultsPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false)
  const [isSessionExpiredOnCreate, setIsSessionExpiredOnCreate] = useState(false)
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | undefined>()
  const [showDebug, setShowDebug] = useState(false)
  const [showExcludedOffers, setShowExcludedOffers] = useState(false)
  const { isAuthenticated } = useAuthStore()
  const { data: debugAccess } = useDebugAccessQuery(isAuthenticated)
  const { filters: searchParams, setFilters } = useFilters(Route.id)
  const { setLastFlightResults } = useFlightSearchStore()
  const restartFlightSearch = useRestartFlightSearch()
  const setPricePreview = useBookingPricePreviewStore((state) => state.setPricePreview)

  // Parse flight search data from URL with Zod validation
  const flightSearchData = useMemo((): FlightSearchSchemaType => {
    try {
      const parsed = searchParams.flightSearch as Record<string, unknown>
      const dataWithDates = {
        ...parsed,

        flight: (parsed?.flight as { date: { from: string; to?: string } }[])?.map((flight) => ({
          ...flight,
          date: {
            from: new Date(flight.date.from),
            to: flight.date.to ? new Date(flight.date.to) : undefined,
          },
        })),
      }

      const validationResult = flightSearchSchema.safeParse(dataWithDates)
      if (validationResult.success) {
        return validationResult.data
      }
      return DEFAULT_FLIGHT_SEARCH
    } catch {
      return DEFAULT_FLIGHT_SEARCH
    }
  }, [searchParams.flightSearch])

  // Parse filter state from URL
  const filterState = useMemo((): FlightFiltersState => {
    // La règle des bornes vit dans `boundsToRange` — partagée avec
    // /vols-multi/resultats, où le chat pose des plafonds sans plancher.
    const priceRange: PriceRange | undefined = boundsToRange(
      searchParams.filterPriceMin,
      searchParams.filterPriceMax
    )

    const durationRange: DurationRange | undefined = boundsToRange(
      searchParams.filterDurationMin,
      searchParams.filterDurationMax
    )

    return {
      durationRange,
      excludedAirlines: searchParams.filterAirlines ? searchParams.filterAirlines.split(',') : [],
      luggage: searchParams.filterLuggage ?? '1',
      priceRange,
      stops: searchParams.filterStops ?? 'all',
      timeFilters: parseFilterTimeRanges(searchParams.filterTimeRanges),
    }
  }, [
    searchParams.filterAirlines,
    searchParams.filterDurationMax,
    searchParams.filterDurationMin,
    searchParams.filterLuggage,
    searchParams.filterPriceMax,
    searchParams.filterPriceMin,
    searchParams.filterStops,
    searchParams.filterTimeRanges,
  ])

  // Convert NewFlightSchema to API request format
  const searchRequest = useMemo((): SearchFlightsRequest => {
    const flights: SearchFlightsRequest['flights'] = []

    // Handle all trip types uniformly
    for (const flight of flightSearchData.flight) {
      if (flight.date.from === undefined) continue

      flights.push({
        date: format(flight.date.from, 'yyyy-MM-dd'),
        departure: {
          category: flight.departure.category,
          iataCode: flight.departure.value,
        },
        destination: {
          category: flight.destination.category,
          iataCode: flight.destination.value,
        },
      })

      // For round-trip, add return flight
      if (flightSearchData.type === 'round-trip' && flight.date.to) {
        flights.push({
          date: format(flight.date.to, 'yyyy-MM-dd'),
          departure: {
            category: flight.destination.category,
            iataCode: flight.destination.value,
          },
          destination: {
            category: flight.departure.category,
            iataCode: flight.departure.value,
          },
        })
      }
    }

    return {
      flights,
      passengers: flightSearchData.passengers,
      // Présent uniquement au retour d'un « relancer la recherche » : le serveur
      // contourne alors son cache pour rendre un `transactionKey` neuf.
      ...(searchParams.refresh ? { refresh: true } : {}),
      travelClass: flightSearchData.travelClass,
    }
  }, [flightSearchData, searchParams.refresh])

  const { data: results, error, isLoading, refetch } = useSearchFlightsQuery(searchRequest)
  const { isPending: isCreatingSession, mutateAsync: createBookingSession } =
    useCreateBookingSession()
  const handleSelectFlight = useCallback(
    async (flight: FlightCardData, selectedJourneyKeys: string[]) => {
      const origin = flightSearchData.flight[0].departure.label
      const destination =
        flightSearchData.flight[flightSearchData.flight.length - 1].destination.label

      setLastFlightResults(searchParams as Record<string, unknown>, '/vols/resultats')

      const departureDate = flightSearchData.flight[0].date.from
        ? format(flightSearchData.flight[0].date.from, 'yyyy-MM-dd')
        : ''
      const returnDate = flightSearchData.flight[0].date.to
        ? format(flightSearchData.flight[0].date.to, 'yyyy-MM-dd')
        : undefined

      try {
        const session = await createBookingSession({
          accountCode: flight.pricing.accountCode,
          departureDate,
          destination,
          journeyKeys: selectedJourneyKeys,
          offerKey: flight.offerKey,
          origin,
          passengersCount: flightSearchData.passengers,
          returnDate,
          // La liste réunit deux transactions Affidium (multiplexage bagage) : c'est
          // la clé portée par l'offre qui fait foi, celle de la réponse ne vaut que
          // pour l'une des deux moitiés.
          transactionKey: flight.sourceTransactionKey ?? results?.transactionKey ?? '',
          tripType: flightSearchData.type,
        })

        // Aperçu du tarif pour la page suivante : le price Affidium prend plusieurs
        // secondes, l'offre qu'on vient de cliquer porte déjà tout sauf les conditions.
        setPricePreview(session.sessionId, toPricePreview(flight, selectedJourneyKeys))

        void navigate({
          search: { session: session.sessionId },
          to: '/informations',
        })
      } catch (error) {
        if (isExpiredSearchSessionError(error)) {
          setSessionExpiredMessage(getApiErrorMessage(error))
          setIsSessionExpiredOnCreate(true)
          return
        }

        throw error
      }
    },
    [
      createBookingSession,
      flightSearchData,
      navigate,
      results?.transactionKey,
      searchParams,
      setLastFlightResults,
      setPricePreview,
    ]
  )

  const filteredOffers = useMemo(() => {
    if (!results?.offers) return []
    return sortOffers(applyFilters(results.offers, filterState), searchParams.filterSort)
  }, [results?.offers, filterState, searchParams.filterSort])

  // Debug : les offres exclues passent par les mêmes filtres que la liste principale, sinon
  // impossible d'isoler « pourquoi CETTE offre-là a sauté » au milieu de plusieurs dizaines.
  const filteredExcludedOffers = useMemo(() => {
    if (!results?.excludedOffers) return []
    return sortOffers(applyFilters(results.excludedOffers, filterState), searchParams.filterSort)
  }, [results?.excludedOffers, filterState, searchParams.filterSort])

  // Calculate active filters count for mobile button
  const activeFiltersCount = useMemo(() => {
    if (!results?.offers) return 0
    let count = 0
    if (filterState.excludedAirlines.length > 0) count++
    if (filterState.stops !== 'all') count++
    // Les bagages ont leur propre CTA dans la barre mobile : les compter ici
    // afficherait « 1 » en permanence alors qu'aucun filtre supplémentaire n'est posé.
    if (filterState.priceRange) count++
    if (filterState.durationRange) count++
    if (filterState.timeFilters.length > 0) count++
    return count
  }, [filterState, results?.offers])

  return (
    <DebugProvider value={showDebug}>
      <FiltersStateProvider routeId={Route.id}>
        <div className="min-h-screen w-screen bg-blue-50">
          {/* Search bar — masquée sur mobile, remplacée par la barre collante ci-dessous */}
          <div className="flex-1 border-b border-gray-200 bg-white py-4 shadow-sm">
            <div className="px-4 md:mb-3 md:px-20">
              <ResultsBackHome />
            </div>
            {/* Démonté sur mobile plutôt que masqué en CSS : deux instances du
                formulaire cohabiteraient sinon avec celle de l'overlay. */}
            {!isMobile && (
              <div className="flex flex-col items-center gap-6 px-4 md:flex-row md:items-center md:px-20">
                {debugAccess?.allowed && (
                  <div className="hidden flex-row items-center gap-2 md:flex">
                    <Label>Debug</Label>
                    <Switch
                      className={`text-xs hover:cursor-pointer ${showDebug ? 'font-semibold text-orange-500' : 'text-gray-400'}`}
                      onCheckedChange={(checked) => {
                        setShowDebug(checked)
                      }}
                    >
                      Debug
                    </Switch>
                  </div>
                )}
                {showDebug && results && (
                  <div className="hidden rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700 md:block">
                    {results.offers.length} offres ·{' '}
                    {results.offers.filter((o) => o.sourceVariant === 'free').length} libre /{' '}
                    {results.offers.filter((o) => o.sourceVariant === 'forced').length} forcé ·{' '}
                    {results.offers.filter((o) => o.minCheckedBags >= 1).length} avec bagage
                  </div>
                )}
                <div className="w-full md:flex md:flex-1 md:justify-center">
                  {/* w-full : sans lui le formulaire se réduit à son contenu sous
                      le justify-center et n'occupe qu'un tiers d'un grand écran. */}
                  <div className="w-full max-w-[1280px]">
                    <FlightSearchForm initialValues={flightSearchData} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {isMobile && (
            <>
              <MobileResultsToolbar
                activeFiltersCount={activeFiltersCount}
                filtersDisabled={!results || results.offers.length === 0}
                onOpenFilters={() => {
                  setIsFiltersModalOpen(true)
                }}
                onOpenSearch={() => {
                  setIsSearchOverlayOpen(true)
                }}
                summary={buildFlightSearchSummary(flightSearchData)}
              />
              <SearchFormOverlay
                isOpen={isSearchOverlayOpen}
                onClose={() => {
                  setIsSearchOverlayOpen(false)
                }}
              >
                {/* La soumission recharge la page (window.location.assign) :
                    inutile de refermer l'overlay à la main. */}
                <FlightSearchForm initialValues={flightSearchData} />
              </SearchFormOverlay>
            </>
          )}

          {isSessionExpiredOnCreate ? (
            <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
              <SessionExpiredError
                message={sessionExpiredMessage}
                onAction={() => {
                  restartFlightSearch({ search: searchParams, to: '/vols/resultats' })
                }}
              />
            </div>
          ) : (
            <>
              {/* No results - full width layout */}
              {results?.offers.length === 0 && (
                <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
                  <NoResultsCard
                    departureLabel={flightSearchData.flight[0].departure.label}
                    destinationLabel={
                      flightSearchData.flight[flightSearchData.flight.length - 1].destination.label
                    }
                  />
                </div>
              )}

              {/* Error - full width layout */}
              {error && (
                <div className="flex flex-1 items-center justify-center px-4 py-8 md:px-20 md:py-12">
                  <FlightErrorCard
                    onRetry={() => {
                      void refetch()
                    }}
                    type={(() => {
                      if (isProviderUnavailableError(error)) return 'maintenance'
                      const scope = getRateLimitScope(error)
                      if (scope === 'day') return 'rate-limited-day'
                      if (scope === 'minute') return 'rate-limited-minute'
                      return 'error'
                    })()}
                  />
                </div>
              )}

              {/* Main content with filters sidebar */}
              {(isLoading || (results && results.offers.length > 0)) && (
                <div className="w-full flex-1 gap-4 px-4 py-4 md:grid md:grid-cols-[1fr_3fr] md:gap-6 md:px-20 md:py-8">
                  {/* Filters sidebar - Desktop only */}
                  {!isMobile && (
                    <aside className="h-fit min-w-0">
                      {results && results.offers.length > 0 && (
                        <FlightFilters offers={results.offers} />
                      )}
                      {isLoading && <FiltersSkeleton />}
                    </aside>
                  )}

                  {/* Flight list */}
                  <main className="min-w-0">
                    {/* Loading modal */}
                    <FlightSearchLoadingModal
                      destinationIataCode={
                        flightSearchData.flight[flightSearchData.flight.length - 1].destination
                          .value
                      }
                      onCancel={() => {
                        void navigate({ to: '/recherche' })
                      }}
                      open={isLoading}
                      originIataCode={flightSearchData.flight[0].departure.value}
                    />

                    {isLoading && (
                      <div className="w-full space-y-4">
                        <div className="mb-4 h-5 w-32 animate-pulse rounded bg-gray-200" />
                        {Array.from({ length: 4 }).map((_, i) => (
                          <FlightCardSkeleton key={i} />
                        ))}
                      </div>
                    )}

                    {results && results.offers.length > 0 && (
                      <>
                        {/* Info banners */}
                        <ResultsBanners flightSearchData={flightSearchData} />

                        {/* Results count + sort */}
                        <div className="mb-4 flex items-center justify-between gap-3">
                          <div className="text-sm text-gray-600">
                            {filteredOffers.length} vol{filteredOffers.length > 1 ? 's' : ''} trouvé
                            {filteredOffers.length > 1 ? 's' : ''}
                            {filteredOffers.length !== results.offers.length && (
                              <span className="text-gray-400">
                                {' '}
                                {/* Mobile : retour à la ligne imposé, pour que le
                                    compte reste sur deux lignes stables quel que
                                    soit le nombre de chiffres. */}
                                <br className="md:hidden" />
                                (sur {results.offers.length} au total)
                              </span>
                            )}
                          </div>
                          <Select
                            onValueChange={(value) => {
                              setFilters({
                                filterSort: value === 'default' ? undefined : value,
                              })
                            }}
                            value={searchParams.filterSort ?? 'default'}
                          >
                            <SelectTrigger className="w-auto min-w-[10rem] gap-2 bg-white">
                              <ArrowUpDown className="text-brand-600 h-4 w-4" />
                              <SelectValue placeholder="Trier" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="default">Recommandé</SelectItem>
                              <SelectItem value="price-asc">Prix croissant</SelectItem>
                              <SelectItem value="price-desc">Prix décroissant</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {filteredOffers.length === 0 ? (
                          <FilteredEmptyCard
                            onReset={() => {
                              setFilters(RESET_FILTERS_PATCH)
                            }}
                          />
                        ) : (
                          <FlightList
                            isLoading={isCreatingSession}
                            offers={filteredOffers}
                            onSelectFlight={(flight, selectedJourneyKeys) => {
                              void handleSelectFlight(flight, selectedJourneyKeys)
                            }}
                            transactionKey={results.transactionKey}
                          />
                        )}

                        {/* Debug: Offres exclues par le post-traitement */}
                        {showDebug && results.excludedOffers.length > 0 && (
                          <div className="mt-6">
                            <button
                              className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                              onClick={() => setShowExcludedOffers((v) => !v)}
                            >
                              {showExcludedOffers ? 'Masquer' : 'Afficher'} les offres exclues (
                              {filteredExcludedOffers.length} sur {results.excludedOffers.length})
                            </button>
                            {showExcludedOffers &&
                              (filteredExcludedOffers.length === 0 ? (
                                // Message dédié plutôt que le « Aucun vol trouvé » de FlightList :
                                // ici il y a bien des offres exclues, ce sont les filtres qui les cachent.
                                <div className="rounded border border-red-200 bg-red-50/50 px-3 py-2 text-sm text-red-700">
                                  Les {results.excludedOffers.length} offre
                                  {results.excludedOffers.length > 1 ? 's' : ''} exclue
                                  {results.excludedOffers.length > 1 ? 's sont toutes' : ' est'}{' '}
                                  écartée{results.excludedOffers.length > 1 ? 's' : ''} par les
                                  filtres actifs.
                                </div>
                              ) : (
                                <div className="space-y-4 opacity-50">
                                  <FlightList
                                    isLoading={false}
                                    offers={filteredExcludedOffers}
                                    onSelectFlight={() => {}}
                                    transactionKey={results.transactionKey}
                                  />
                                </div>
                              ))}
                          </div>
                        )}
                      </>
                    )}
                  </main>
                </div>
              )}
            </>
          )}

          {/* Mobile: Filters Modal — hors des branches de contenu, la barre
              collante qui l'ouvre reste montée quel que soit l'état de la page. */}
          {isMobile && results && results.offers.length > 0 && (
            <FiltersModal
              isOpen={isFiltersModalOpen}
              offers={results.offers}
              onClose={() => {
                setIsFiltersModalOpen(false)
              }}
            />
          )}

          {/* Back to top button */}
          <BackToTopButton />
        </div>
      </FiltersStateProvider>
    </DebugProvider>
  )
}
